import React from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Calendar } from "../../ui/calendar";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { addHoursToTime } from "../../../utils/dateUtils";

const ScheduleSection = ({ formData, setFormData, getCurrentTime }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-[#FFCC00]">
        <CalendarIcon className="w-4 h-4" />
        <span className="text-sm font-medium">Schedule</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-theme rounded-lg p-2 flex justify-center bg-theme-surface overflow-x-auto">
          <Calendar
            mode="single"
            selected={formData.scheduled_date}
            onSelect={(date) => date && setFormData(prev => ({ ...prev, scheduled_date: date }))}
            className="rounded-md"
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "text-sm font-medium text-foreground",
              nav: "space-x-1 flex items-center",
              nav_button: "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-foreground hover:bg-theme-hover rounded-md inline-flex items-center justify-center",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse space-y-1",
              head_row: "flex",
              head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
              row: "flex w-full mt-2",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FFCC00] [&:has([aria-selected])]:rounded-md",
              day: "h-8 w-8 p-0 font-normal text-foreground hover:bg-theme-hover rounded-md inline-flex items-center justify-center",
              day_selected: "bg-[#FFCC00] text-black hover:bg-[#FFCC00] hover:text-black focus:bg-[#FFCC00] focus:text-black",
              day_today: "bg-muted text-foreground font-semibold",
              day_outside: "text-muted-foreground/50",
              day_disabled: "text-muted-foreground/30 opacity-50",
              day_hidden: "invisible",
            }}
          />
        </div>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm"
              onClick={() => setFormData(prev => ({ ...prev, scheduled_date: new Date(), scheduled_time: getCurrentTime() }))}
              className="flex-1 border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10"
              data-testid="schedule-now-btn"
            >
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Now
            </Button>
            <Button type="button" variant="outline" size="sm"
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(9, 0, 0, 0);
                setFormData(prev => ({ ...prev, scheduled_date: tomorrow, scheduled_time: "09:00" }));
              }}
              className="flex-1 border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10"
              data-testid="schedule-tomorrow-btn"
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Tomorrow 9AM
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={`space-y-1.5 sm:space-y-2 ${formData.schedule_type === "between" ? "col-span-2" : ""}`}>
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground text-xs sm:text-sm">Time</Label>
                <div className="flex bg-theme-surface rounded-md p-0.5 border border-theme">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, schedule_type: "exact" }))}
                    className={`px-2 py-0.5 text-[10px] rounded transition-colors ${(formData.schedule_type || "exact") === "exact" ? "bg-[#FFCC00] text-black font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="schedule-type-exact"
                  >
                    Exact
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      schedule_type: "between",
                      scheduled_time_end: addHoursToTime(prev.scheduled_time, prev.duration_hours) || prev.scheduled_time_end,
                    }))}
                    className={`px-2 py-0.5 text-[10px] rounded transition-colors ${formData.schedule_type === "between" ? "bg-[#FFCC00] text-black font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="schedule-type-between"
                  >
                    Between
                  </button>
                </div>
              </div>
              {formData.schedule_type === "between" ? (
                <div className="flex items-center gap-2">
                  <Input type="time" value={formData.scheduled_time} onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value, scheduled_time_end: addHoursToTime(e.target.value, prev.duration_hours) || prev.scheduled_time_end }))} className="input-glass flex-1 min-w-[110px]" data-testid="schedule-time-input" />
                  <span className="text-xs text-muted-foreground shrink-0">to</span>
                  <Input type="time" value={formData.scheduled_time_end || ""} onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time_end: e.target.value }))} className="input-glass flex-1 min-w-[110px]" data-testid="schedule-time-end-input" />
                </div>
              ) : (
                <div className="flex gap-1">
                  <Input type="time" value={formData.scheduled_time} onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))} className="input-glass flex-1" data-testid="schedule-time-input" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setFormData(prev => ({ ...prev, scheduled_time: getCurrentTime() }))} className="h-9 w-9 text-muted-foreground hover:text-[#FFCC00] hover:bg-theme-surface" title="Set to current time">
                    <Clock className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-muted-foreground text-xs sm:text-sm">Duration (hrs)</Label>
              <Input type="number" step="0.25" min="0.25" value={formData.duration_hours} onChange={(e) => setFormData(prev => ({ ...prev, duration_hours: e.target.value, ...(prev.schedule_type === "between" && prev.scheduled_time ? { scheduled_time_end: addHoursToTime(prev.scheduled_time, e.target.value) || prev.scheduled_time_end } : {}) }))} className="input-glass" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSection;
